import React, { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";
import styled from "styled-components";
import { ImageLoader, ButtonText, LOAN_TYPES } from "@loanbossinc/component-library";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { compose } from "redux";
import { Formik } from "formik";
import { getConfigItemByParentId } from "store/state/configItems";
import {
  getRefiScenario,
  defavoriteScenario,
  favoriteScenario,
  cashoutOneOffCalc
} from "pages/dealAnalysis/dealAnalysis.store";

import SupervisedAssumptionCard from "./SupervisedAssumptionCard";

import { map as DEAL_METRIC } from "constants/screenElements/dealViewMetric";

import { FavoriteButton } from "./FavoriteButton";
import { FavoriteModal } from "./FavoriteModal";
import { PieChart } from "./PieChart";
import { RefiSavingsModule } from "./RefiSavingsModule";
import RefiBreakevenModule from "./RefiBreakevenModule";

import { CASHOUT_PROCEEDS_PREFERRED_CONSTRAINT } from "constants/configItemParents";
import { LoanProceedsTooLowBanner } from "components/Calculators/TestHarness";

import { LenderLoanTypeList } from "../../../constants/loanTypes";
import { dealGet } from "../../deal/deal.store";
import { LBFooter } from "../../../components/LBFooter";
import "./styles.css";
import {
  floatingIndexList,
  fixedIndexList
} from "../../../constants";
import InterestRateType from "../../../components/Calculators/Constants/InterestRateType";
import { ToastError } from "../../../components/Global/Toasts/Toasty";

import Accordian from "./Accordian";

const StyledImgLoader = styled(ImageLoader)`&& {
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
}`;

const PieOptions = {
  "loanBalance": {
    color: "hsl(198, 87%, 45%)",
    label: "Loan Balance",
    testValue: 500000
  },
  "prepaymentPenalty": {
    color: "hsl(0, 0%, 77%)",
    label: "Prepayment Penalty",
    testValue: 87805
  },
  "closingCosts": {
    color: "hsl(213, 5%, 39%)",
    label: "Closing Costs",
    testValue: 100000
  },
  "cashout": {
    color: "hsl(208, 100%, 33%)",
    label: "Net Cash Out",
    testValue: 643194
  },
  "netCashout": {
    color: "hsl(208, 100%, 33%)",
    label: "Net Cash Out",
    testValue: 643194
  }
};

const initialValues = {
  lpsLoanType: "",
  lpsRateType: "",
  lpsRateIndex: "",
  lpsClosingCosts: "",
  lpsAmortizationType: "",
  lpsAmortizationTerm: "",
  lpsAmortizationTermForCashflows: "",
  lpsAmortizationRateForCashflows: "",
  lpsAmortizationRate: "",
  lpsIoPeriods: "",
  lpsDaycount: "",
  lpsConstraintPreferenceId: "",
  dscrMin: "",
  ltvMax: "",
  spread: "",
  debtYieldMin: ""
};

const ErrorBanner = ({ message }) => <div className="supervised-too-low-banners"><LoanProceedsTooLowBanner message={message} /></div>;
const fixedRateIndexIds = fixedIndexList.map(l => l.value);
const floatingRateIndexIds = floatingIndexList.map(l => l.value);
const defaultImageUrl = process.env.PUBLIC_URL + "/images/default-banner.png";
const RefiAnalysisSupervised = ({
  dealGet,
  deal,
  match,
  getRefiScenario,
  configItems,
  getConfigItemByParentId,
  refiScenario,
  defavoriteScenario,
  favoriteScenario,
  cashoutOneOffCalc,
  calculationResults,
  authentication,
  history
}) => {
  const isSystemAdmin = authentication.isSystemAdmin;
  const { configItemByParent = [] } = configItems;
  const { dealId, loanSpreadId } = match.params;
  const [isModalOpen, setModalOpen] = useState(false);
  const [refiData, setRefiData] = useState({});
  const [isPositiveNetCashout, setIsPositiveNetCashout] = useState(false);
  const [preferredConstraintsList, setPreferredConstraintsList] = useState([]);
  const [showOneOffCalc, setShowOneOffCalc] = useState(false);
  const [cashoutAnaylsisValues, setCashoutAnaylsisValues] = useState([]);
  const [ltvMax, setLtvMax] = useState(0);
  const [amortTermYears, setAmortTermYears] = useState(0);
  const [loanTermYears, setLoanTermYears] = useState(0);
  const [loanType, setLoanType] = useState(LOAN_TYPES.BALANCE_SHEET);
  const [loanTypeName, setLoanTypeName] = useState("");
  const [capRate, setCapRate] = useState(0);
  const [termForChart, setTermForChart] = useState(1);
  const [netCashout, setNetCashout] = useState(null);
  const [isFixedToFixed, setIsFixedToFixed] = useState(1);
  const [noi, setNoi] = useState(0);

  const refiResults = (showOneOffCalc && calculationResults?.[0]) ? calculationResults : refiScenario?.scenario;

  useEffect(() => {
    if (!deal) {
      dealGet({ dealId });
    }
    if (Object.keys(refiScenario).length === 0) {
      getRefiScenario({ dealId, loanSpreadId });
    }
    getConfigItemByParentId({ id: CASHOUT_PROCEEDS_PREFERRED_CONSTRAINT });
  }, []);

  useEffect(() => {
    if (deal) {
      setCapRate(deal[DEAL_METRIC.CAP_RATE]?.decimalValue);
      setNoi(deal[DEAL_METRIC.NOI_ACTUAL]?.decimalValue);
    }
  }, [deal]);

  useEffect(() => {
    const formattedList = configItemByParent[CASHOUT_PROCEEDS_PREFERRED_CONSTRAINT]?.map(({ id, value }) => ({ name: value, label: value, value: id }));
    const preferredConstraintListFormatted = formattedList ? [{ name: "None", label: "", value: "" }, ...formattedList] : [];
    setPreferredConstraintsList(preferredConstraintListFormatted);
  }, [configItems]);

  useEffect(() => {
    const refiResultsForNetCashout = (showOneOffCalc && calculationResults) ? calculationResults : refiScenario?.scenario;

    setIsFixedToFixed(refiResultsForNetCashout?.isFixedToFixed);
    setNetCashout((!showOneOffCalc) ? refiResultsForNetCashout?.cashout : calculationResults.netCashout);
    // Pie Chart Values
    const cashoutValues = ["loanBalance", "prepaymentPenalty", "closingCosts", (showOneOffCalc) ? "netCashout" : "cashout"].map(val => {
      const pieOption = PieOptions[val];
      const value = (refiResultsForNetCashout) ? refiResultsForNetCashout[val] : 0;

      return {
        id: val,
        label: pieOption.label,
        value,
        color: pieOption.color
      };
      // removing any objects with a value of 0
    }).filter(coValueObj => coValueObj.value !== 0);

    const sortValuesForChart = ([...array]) => {
      // sorting to order from smallest to largest
      array.sort((a, b) => a.value - b.value);

      // since we can expect only arrays of three or four objects, return an array with the objects ordered in a way that displays cleanly on the chart
      return array.length === 4 ? [array[3], array[0], array[2], array[1]] : [array[0], array[2], array[1]];
    };

    setCashoutAnaylsisValues(sortValuesForChart(cashoutValues).filter(item => item));

    // Refi Breakeven or Refi Analysis Determination
    setIsPositiveNetCashout(refiScenario?.scenario?.netCostToRefi > 0);

    // Setting Values
    setLtvMax(refiScenario?.scenario?.spread?.constraint?.ltvMax ?? 0);
    setAmortTermYears(refiScenario?.scenario?.spread?.loanTypeSetting?.amortTermMonths / 12 ?? 0);
    setLoanTermYears(refiScenario?.scenario?.spread?.term ?? 0);
    setLoanType(refiScenario?.scenario?.spread?.loanTypeSetting?.loanType?.value);
    setLoanTypeName(LenderLoanTypeList.find(lt => refiScenario?.scenario?.spread?.loanTypeSetting?.loanType?.value === lt.value)?.label ?? "Unknown Loan Type");
    setTermForChart(refiScenario?.scenario?.spread?.term);
  }, [refiScenario, refiResults, calculationResults, showOneOffCalc]);

  useEffect(() => {
    const refiResultsForRefi = (showOneOffCalc && calculationResults) ? calculationResults : refiScenario?.scenario;
    if (showOneOffCalc) {
      refiResultsForRefi.PV01 = refiResultsForRefi.pv01;
    }

    const reduceRefiData = arrOfKeys => arrOfKeys.reduce((acc, value) => {
      const result = (refiResultsForRefi) ? refiResultsForRefi[value] : 0;
      acc[value] = result;

      return acc;
    }, {});

    if (isPositiveNetCashout) {
      setRefiData(reduceRefiData(["currentTreasury", "breakevenTreasury", "breakevenSpread", "PV01", "prepaymentPenalty", "interestSavings", "netCostToRefi"]));
    } else {
      setRefiData(reduceRefiData(["refiLoanInterest", "prepaymentPenalty", "interestSavings", "currentLoanInterest", "netCostToRefi"]));
    }
  }, [isPositiveNetCashout, refiScenario, showOneOffCalc, calculationResults]);

  const isLoading = (!loanType || !loanTermYears || !amortTermYears || !ltvMax || deal === null);
  const subtitle = isLoading ? "Loading..." : `${loanTypeName}, ${parseInt(loanTermYears, 10)}x${parseInt(amortTermYears, 10)}, ${Math.round(ltvMax * 100)}% LTV`;

  const handleFavoriteButton = () => {
    setModalOpen(true);
  };

  const onSave = () => {
    if (refiScenario.isFavorite) {
      // deFavorite
      defavoriteScenario({ dealId, loanSpreadId });
      setModalOpen(false);

      return;
    }

    /*
     * favorite
     * TODO - copy of state here
     */
    favoriteScenario({ dealId, loanSpreadId, noi, capRate });
    setModalOpen(false);
  };

  const handleCalculateClick = (params, fave) => {
    const paramsToSend = {
      ...params,
      debtYieldMin: Number(params.debtYieldMin) / 100,
      futureCapRate: Number(params.futureCapRate),
      spread: Number(params.spread) / 100,
      lpsClosingCosts: Number(params.lpsClosingCosts) / 100,
      ltvMax: Number(params.ltvMax / 100),
      loanTerm: Number(params.loanTerm),
      noi: Number(params.noi),
      lpsAmortizationTerm: Number(params.lpsAmortizationTerm),
      lpsAmortizationTermForCashflows: Number(params.lpsAmortizationTermForCashflows),
      lpsIoPeriods: Number(params.lpsIoPeriods),
      lpsAmortizationRate: Number(params.lpsAmortizationRate) ?? 0,
      lpsAmortizationRateForCashflows: Number(params.lpsAmortizationRateForCashflows) ?? 0,
      uwRate: Number(params.uwRate) ?? 0,
      isFavorite: fave
    };

    cashoutOneOffCalc({ params: paramsToSend, dealId, loanSpreadId, callback: () => ((fave) ? getRefiScenario({ dealId, loanSpreadId }) : () => { /* NO OPERATION */ }) });
  };

  const treasuryLabel = `${refiScenario?.scenario?.spread?.term}T`;

  return (
    <div className="supervised-analysis-page-container">
      <StyledImgLoader src={deal ? deal.imageLarge : ""} defaultImage={defaultImageUrl} height="320px" width="1120px" style={{ margin: "0 auto" }} />
      <Grid container className="supervised-analysis-page-grid sap-grid-top">
        <Grid item xs={12} className="supervised-go-back-grid-wrapper">
          <ButtonText
            onClick={() => history.goBack()}
            iconName="arrow_forward"
            label="Go Back"
            reverse
          />
        </Grid>
        <Grid container item xs={12} alignItems="center">
          <div className="supervised-analysis-header-wrapper">
            <Grid item xs={6}>
              <div>Refinance Analysis</div>
            </Grid>
            <Grid item xs={6} className="supervised-analysis-favorite-grid-wrapper">
              <FavoriteButton
                isFavorite={refiScenario.isFavorite}
                onClick={handleFavoriteButton}
              />
              <FavoriteModal
                isFavorite={refiScenario.isFavorite}
                onClose={() => {
                  setModalOpen(false);
                }}
                isOpen={isModalOpen}
                onSave={onSave}
              />
            </Grid>
          </div>
        </Grid>
      </Grid>
      <Grid container className="supervised-analysis-page-grid sap-grid-bottom">
        <Grid item xs={12}>
          <Formik
            initialValues={initialValues}
            onReset={(initialValues, form) => {
              setShowOneOffCalc(false);
              form.resetForm(initialValues);
            }}
            onSubmit={values => {
              const isValidRateSelection = values.lpsRateType === InterestRateType.FIXED_RATE_ID ? fixedRateIndexIds.indexOf(values.lpsRateIndex) >= 0 : floatingRateIndexIds.indexOf(values.lpsRateIndex) >= 0;
              if (isValidRateSelection) {
                                refiScenario?.isFavorite ? setShowOneOffCalc(false) : setShowOneOffCalc(true);
                                handleCalculateClick(values, refiScenario.isFavorite);
              } else {
                ToastError("Please select a Rate Index");
              }
            }}
            render={formikProps => <SupervisedAssumptionCard
              isLoading={isLoading}
              preferredConstraintsList={preferredConstraintsList}
              savedAssumptions={refiScenario?.scenario}
              isFavorite={refiScenario?.isFavorite}
              favoriteAssumption={refiScenario?.loanProceedAssumption}
              subtitle={subtitle}
              noi={noi}
              capRate={capRate}
              loanTerm={refiScenario?.scenario?.spread?.term}
              onReset={() => {
                setShowOneOffCalc(false);
              }}
              {...formikProps}
              style={{ background: "#FFF" }}
            />}
          />
        </Grid>
        {
          netCashout >= 0 &&
            <div className="supervised-analysis-cashout-and-modules-wrapper">
              <Grid item xs={12}>
                <Accordian name="Net Cashout Analysis" showDownload={false}>
                  <PieChart data={cashoutAnaylsisValues} />
                </Accordian>
              </Grid>
              {
                (isSystemAdmin && Boolean(isFixedToFixed)) &&
                  <Grid item xs={12}>
                    {
                      (refiScenario && isPositiveNetCashout && refiData?.breakevenSpread >= 0) ?
                        <Accordian name="Refi Breakeven Analysis" showDownload={false}>
                          <RefiBreakevenModule data={refiData} treasuryLabel={treasuryLabel} />
                        </Accordian> :
                        <div />
                    }
                    {
                      (refiScenario && !isPositiveNetCashout && refiData) ?
                        <Accordian name="Refi Savings Analysis" showDownload={false}>
                          <RefiSavingsModule data={refiData} term={termForChart} />
                        </Accordian> :
                        <div />
                    }
                  </Grid>
              }
              {(isSystemAdmin && !isFixedToFixed) && <ErrorBanner message="Breakeven and savings analysis require that both existing and prospective refi loans need to be fixed rates." />}
            </div>
        }
        {netCashout < 0 && <ErrorBanner message="Loan Proceeds too low to refinance the current loan(s). Please try another structure." />}
        <div className="refi-analysis-supervised-footer-container">
          <LBFooter />
        </div>
      </Grid>
    </div>
  );
};

const mapStateToProps = ({ newDeal, dealAnalysis, configItems, authentication }) => ({
  deal: newDeal.deal,
  refiScenario: dealAnalysis.refiScenario,
  calculationResults: dealAnalysis.calculationResults,
  configItems,
  authentication
});

export default compose(withRouter, connect(mapStateToProps, {
  dealGet,
  getRefiScenario,
  defavoriteScenario,
  favoriteScenario,
  getConfigItemByParentId,
  cashoutOneOffCalc
}))(RefiAnalysisSupervised);
